import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '@/store/store';
import {
  createRule,
  updateRule,
  clearError,
  clearSuccessMessage,
  fetchRuleById,
  clearSingleRule,
  clearDeleteError,
  clearSingleRuleError,
} from '@/store/rulesSlice';
import AppLayout from '@/components/layout/AppLayout';
import Navbar from '@/components/ui/navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import Typography from '@/components/ui/typography';
import {
  ArrowLeft,
  Plus,
  Loader2,
  Edit,
  Trash2,
  MoreVertical,
  Power,
  PowerOff,
} from 'lucide-react';
import { RouteConstants } from '@/constants/route-constant';
import RuleConditionIcon from '@/assets/Svg/ruleCondition.svg';
import MultiSelectChips from '../../components/ui/MultiSelectChips';
import useFields from '@/hooks/useFields';
import { Field, FieldOperators } from '@/types/api';
import { useToastHelpers, TOAST_MESSAGES } from '@/utils/toast';
import DeleteRuleModal from '@/components/dashboard/DeleteRuleModal';
import { usePermissions } from '@/hooks';
import { MatchBlock } from '@/store/rulesSlice';

interface RuleCondition {
  uuid?: string; // Add UUID for existing matchBlocks
  field: string[];
  operator: string; // This will now store the operator UUID
  value: string;
  //added
  dropdownValue?: string;
  selectedPayers?: string[];
  // Added this
  selectedProviders?: string[]; // Changed to array for multi-select
  selectedCredentials?: string[];
}

interface RuleFormProps {
  mode: 'create' | 'edit';
}

const RuleForm: React.FC<RuleFormProps> = ({ mode }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { id } = useParams();
  const { selectedHospital } = useSelector(
    (state: RootState) => state.hospitals,
  );
  const {
    loading,
    error,
    successMessage,
    singleRule,
    singleRuleLoading,
    singleRuleError,
    deleteLoading,
  } = useSelector((state: RootState) => state.rules);
  const {
    fields: fieldsData,
    loading: fieldsLoading,
    error: fieldsError,
  } = useFields();

  const { showSuccess, showError } = useToastHelpers();
  const [ruleName, setRuleName] = useState('');
  const [reason, setReason] = useState('');
  const [action, setAction] = useState('');
  const [isPayerSpecific, setIsPayerSpecific] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [payers, setPayers] = useState<string[]>([]);
  const [conditions, setConditions] = useState<RuleCondition[]>([
    { field: [], operator: '', value: '' }, // operator will be set when field is selected
  ]);
  const [ruleStatus, setRuleStatus] = useState<'active' | 'inactive'>('active');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const { hasPermission } = usePermissions();
  const canEditRule = hasPermission('write-rule');
  const canDeleteRule = hasPermission('delete-rule');

  // Transform fields data for MultiSelectChips component
  const fieldOptions = fieldsData.map((field: Field) => ({
    value: field.code,
    label: field.label,
  }));

  // Ensure payers are fetched when component mounts

  // Ensure fields are fetched when component mounts
  useEffect(() => {
    if (
      selectedHospital?.tenantId &&
      fieldsData.length === 0 &&
      !fieldsLoading
    ) {
      // This will trigger the useFields hook to fetch fields
      // The hook will handle the actual API call
    }
  }, [selectedHospital?.tenantId, fieldsData.length, fieldsLoading]);

  // Clear errors and success messages when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearError());
      dispatch(clearSuccessMessage());
      dispatch(clearDeleteError());
    };
  }, [dispatch]);

  // Auto-clear success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        dispatch(clearSuccessMessage());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, dispatch]);

  // Clear error when user starts typing
  useEffect(() => {
    if (error || singleRuleError) {
      const handleInputChange = () => {
        dispatch(clearError());
        dispatch(clearSingleRuleError());
      };

      // Add event listeners to form inputs
      const inputs = document.querySelectorAll('input, textarea');
      inputs.forEach((input) => {
        input.addEventListener('input', handleInputChange);
      });

      return () => {
        inputs.forEach((input) => {
          input.removeEventListener('input', handleInputChange);
        });
      };
    }
  }, [error, singleRuleError, dispatch]);

  // Load rule data if in edit mode
  useEffect(() => {
    if (mode === 'edit' && id && selectedHospital?.tenantId) {
      // Fetch the rule data from the API
      dispatch(
        fetchRuleById({ ruleId: id, tenantId: selectedHospital.tenantId }),
      );
    }

    // Cleanup function to clear single rule when component unmounts
    return () => {
      if (mode === 'edit') {
        dispatch(clearSingleRule());
      }
    };
  }, [mode, id, selectedHospital?.tenantId, dispatch]);

  const getValueInputType = (fieldCode: string, operatorUuid: string) => {
    if (!fieldCode || !operatorUuid) return 'text-input';

    const selectedField = fieldsData.find((field) => field.code === fieldCode);
    const selectedOperator = selectedField?.operators?.find(
      (op) => op.uuid === operatorUuid,
    );

    const fieldLabel = selectedField?.label?.toLowerCase() || '';
    const fieldCodeLower = fieldCode?.toLowerCase() || '';
    const operatorCode = selectedOperator?.code?.toLowerCase() || '';
    const operatorLabel = selectedOperator?.label?.toLowerCase() || '';

    // Global rule: for any field, if operator is "is_empty" or "is_not_empty",
    // disable the input box
    if (
      operatorCode === 'is_empty' ||
      operatorCode === 'is_not_empty' ||
      operatorLabel.includes('empty')
    ) {
      return 'disabled-input';
    }

    // Patient Status field with "Is New" or "Is Not New" operators
    if (
      fieldCodeLower === 'patient_status' &&
      (operatorCode === 'is_new' || operatorCode === 'is_not_new')
    ) {
      return 'disabled-input';
    }

    //Added this
    // Credentials condition - independent of field value
    if (
      operatorCode === 'credentials_are' ||
      operatorLabel === 'credentials are'
    ) {
      return 'dropdown-credentials';
    }

    //Added this
    // Provider fields with non-credentials operators
    const providerFields = [
      'supervising_provider',
      'billing_provider',
      'rendering_provider',
      'referring_provider',
    ];
    if (
      providerFields.includes(fieldCodeLower) &&
      operatorCode !== 'credentials_are'
    ) {
      return 'dropdown-provider';
    }

    // Date of Service field
    if (
      fieldCodeLower === 'date_of_service' ||
      fieldLabel.includes('date of service')
    ) {
      if (
        operatorCode === 'same_day_as_claim' ||
        operatorLabel.includes('same day')
      ) {
        return 'dropdown-claim';
      }
      // Add date input for "IS" operator
      if (
        operatorCode === 'is' ||
        operatorCode === 'equals' ||
        operatorLabel === 'is' ||
        operatorLabel === 'equals'
      ) {
        return 'date-input';
      }
    }

    //multi-select
    if (
      fieldCodeLower === 'payer_medical_insurance' ||
      fieldLabel.includes('payer') ||
      fieldLabel.includes('insurance')
    ) {
      return 'dropdown-payer-multi'; // Changed from 'dropdown-payer'
    }

    // Fee field - all operators use number input
    if (fieldCodeLower === 'fee' || fieldLabel.includes('fee')) {
      return 'number-input';
    }

    return 'text-input';
  };

  // Populate form fields when single rule data is available
  useEffect(() => {
    if (singleRule && mode === 'edit') {
      setRuleName(singleRule.name);
      setReason(singleRule.reason);
      setAction(singleRule.action || '');
      setIsPayerSpecific(singleRule.markRuleForSpecificPayers);
      setPayers(singleRule.payerIds);
      setRuleStatus(singleRule.enabled ? 'active' : 'inactive');

      // Transform matchBlocks to conditions format
      const transformedConditions = singleRule.matchBlocks.map((mb) => {
        // Handle source field - now it's an object with code property
        let sourceFieldCode = '';
        if (typeof mb.source === 'string') {
          // Legacy: Check if it's a UUID (contains hyphens) or a field code
          if (mb.source.includes('-')) {
            // It's a UUID, find the corresponding field code
            const field = fieldsData.find((f) => f.uuid === mb.source);
            sourceFieldCode = field?.code || mb.source;
          } else {
            // It's already a field code
            sourceFieldCode = mb.source;
          }
        } else {
          // New format: source is an object with code property
          sourceFieldCode = mb.source?.code || mb.source?.uuid || '';
        }

        // Handle operator field - now it's an object with uuid property
        let operatorUuid = '';
        if (typeof mb.operator === 'string') {
          // Legacy: operator is a string (UUID or code)
          operatorUuid = mb.operator;
        } else {
          // New format: operator is an object with uuid property
          operatorUuid = mb.operator?.uuid || '';
        }

        //changes added (for matchText input while editing ruleForm)

        const condition: RuleCondition = {
          uuid: mb.uuid,
          field: [sourceFieldCode],
          operator: operatorUuid,
          value: mb.matchTexts?.join(', ') || '',
        };

        const inputType = getValueInputType(sourceFieldCode, operatorUuid);

        if (inputType === 'dropdown-claim') {
          condition.dropdownValue = mb.matchTexts?.[0] || '';
        } else if (inputType === 'dropdown-payer-multi') {
          condition.selectedPayers = mb.matchTexts || []; // This will get the UUIDs from matchTexts
        } else if (inputType === 'dropdown-provider') {
          // For provider dropdowns, the matchTexts contains the NPI values
          condition.selectedProviders = mb.matchTexts || [];
        } else if (inputType === 'dropdown-credentials') {
          // For credentials multi-select, matchTexts contains credential codes
          condition.selectedCredentials = mb.matchTexts || [];
        } else if (inputType === 'date-input') {
          // For date inputs, use the first matchText as the date value
          condition.value = mb.matchTexts?.[0] || '';
        }

        return condition;
      });

      setConditions(
        transformedConditions.length > 0
          ? transformedConditions
          : [{ field: [], operator: '', value: '' }],
      );
    }
  }, [singleRule, mode]);

  const handleAddCondition = () => {
    setConditions([
      ...conditions,
      { field: [], operator: '', value: '' }, // New conditions don't have UUID, operator will be set when field is selected
    ]);
  };

  const handleRemoveCondition = (index: number) => {
    if (conditions.length > 1) {
      const newConditions = conditions.filter((_, i) => i !== index);
      setConditions(newConditions);
    }
  };

  //added this
  const getOperatorsForField = (fieldCode: string) => {
    const selectedField = fieldsData.find((field) => field.code === fieldCode);
    if (!selectedField || !selectedField.operators) return [];

    return selectedField.operators.map((operator) => ({
      value: operator.uuid, // Use UUID as value instead of code
      label: operator.label,
      uuid: operator.uuid,
      description: operator.description,
    }));
  };

  //changes add (15sep)
  const getClaimOptions = (fieldCode: string, operatorUuid: string) => {
    const selectedField = fieldsData.find((field) => field.code === fieldCode);
    const selectedOperator = selectedField?.operators?.find(
      (op) => op.uuid === operatorUuid,
    );

    if (selectedOperator?.references) {
      return selectedOperator.references.map((ref) => ({
        value: ref.uuid,
        label: ref.label,
        uuid: ref.uuid,
      }));
    }

    return [];
  };

  const getPayerOptionsForField = () => {
    const payerField = fieldsData.find(
      (field) => field.code === 'PAYER_MEDICAL_INSURANCE',
    );
    if (payerField?.payers) {
      return payerField.payers
        .map((payer) => ({
          value: payer.athenaPayerId,
          label: payer.name,
          uuid: payer.uuid,
        }))
        .sort((a, b) =>
          a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
        );
    }
    return [];
  };

  const handleConditionChange = (
    index: number,
    field: keyof RuleCondition,
    value: string | string[],
  ) => {
    const newConditions = [...conditions];
    if (newConditions[index]) {
      newConditions[index] = {
        ...newConditions[index],
        [field]: value,
      };
    }

    //added this
    //changes for rule condition input conditional rendering
    if (field === 'field') {
      newConditions[index].operator = '';
      newConditions[index].value = '';
      newConditions[index].dropdownValue = undefined;
      newConditions[index].selectedPayers = []; // Reset selected payers
      newConditions[index].selectedProviders = []; // Added this
      newConditions[index].selectedCredentials = []; // Added this
    }

    if (field === 'operator') {
      newConditions[index].value = '';
      newConditions[index].dropdownValue = undefined;
      newConditions[index].selectedPayers = []; // Reset selected payers
      newConditions[index].selectedProviders = []; // Added this
      newConditions[index].selectedCredentials = []; // Added this

      //changes (16th)
      // Force re-render by ensuring clean state
      const inputType = getValueInputType(
        newConditions[index].field[0],
        value as string,
      );
      if (inputType === 'disabled-input') {
        // For disabled inputs, ensure value is empty and stays empty
        newConditions[index].value = '';
        newConditions[index].dropdownValue = undefined;
        newConditions[index].selectedPayers = [];
      }
    }
    setConditions(newConditions);
  };

  const handleSave = async () => {
    if (!isFormValid) return;
    if (!selectedHospital?.tenantId) {
      console.error('No tenant ID available');
      return;
    }

    // Process match texts - split by comma, trim spaces, and filter out empty strings
    const processedMatchBlocks = conditions.map((condition) => {
      //changes for conditional rendering
      const inputType = getValueInputType(
        condition.field[0],
        condition.operator,
      );
      let matchTexts: string[] = [];

      if (inputType === 'dropdown-claim') {
        matchTexts = condition.dropdownValue ? [condition.dropdownValue] : [];
      } else if (inputType === 'dropdown-payer-multi') {
        // Handle multi-select payers
        matchTexts = condition.selectedPayers || [];
      } else if (inputType === 'dropdown-provider') {
        // Added this
        matchTexts = condition.selectedProviders || [];
      } else if (inputType === 'dropdown-credentials') {
        // Added this
        matchTexts = condition.selectedCredentials || [];
      } else if (inputType === 'disabled-input') {
        // For operators like "is empty" / "is not empty", API expects an
        // array with a single empty string instead of an empty array
        matchTexts = [''];
      } else if (inputType === 'date-input') {
        // For date inputs, use the value directly as a single element array
        matchTexts = condition.value ? [condition.value] : [];
      } else {
        matchTexts = condition.value
          .split(',')
          .map((text) => text.trim())
          .filter((text) => text.length > 0);
      }

      // Determine found field based on operator type
      // For now, we'll assume operators with "contain" in their label are positive matches
      const selectedField = fieldsData.find(
        (field) => field.code === condition.field[0],
      );
      const selectedOperator = selectedField?.operators?.find(
        (op) => op.uuid === condition.operator,
      );

      const found =
        selectedOperator?.label?.toLowerCase().includes('contain') || false;

      // Get the field UUID for the source
      const sourceFieldUuid = selectedField?.uuid || condition.field[0] || '';

      // Preserve UUID for existing matchBlocks, don't include for new ones
      const matchBlock: MatchBlock = {
        matchTexts,
        found,
        source: sourceFieldUuid, // Use field UUID instead of field code
        operator: condition.operator, // Use operator UUID
      };

      // Add UUID only if it exists (for existing matchBlocks)
      if (condition.uuid) {
        matchBlock.uuid = condition.uuid;
      }

      return matchBlock;
    });

    const ruleData = {
      name: ruleName.trim(),
      reason: reason.trim(),
      action: action.trim() || null,
      enabled: ruleStatus === 'active' ? true : false,
      version: '1.0', // Always 1.0 as per requirements
      markRuleForSpecificPayers: isPayerSpecific,
      payerIds: isPayerSpecific ? payers : [],
      matchBlocks: processedMatchBlocks,
    };

    try {
      if (mode === 'create') {
        await dispatch(
          createRule({
            ruleData,
            tenantId: selectedHospital.tenantId,
          }),
        ).unwrap();
        // Show success toast and navigate back to dashboard
        showSuccess(TOAST_MESSAGES.SUCCESS_CREATE_RULE);
        navigate(RouteConstants.ROUTE_HOME);
      } else if (mode === 'edit' && isEditing && id) {
        // Update existing rule
        await dispatch(
          updateRule({
            ruleId: id,
            ruleData,
            tenantId: selectedHospital.tenantId,
          }),
        ).unwrap();
        // Show success toast and exit edit mode
        showSuccess(TOAST_MESSAGES.SUCCESS_UPDATE_RULE);
        setIsEditing(false);
      }
    } catch (error: unknown) {
      console.error('Failed to save rule:', error);
      // Extract error message from the rejected action payload
      // When using .unwrap(), rejectWithValue throws the value directly
      let errorMessage = '';
      if (typeof error === 'string') {
        errorMessage = error;
      } else if (error && typeof error === 'object' && 'message' in error) {
        errorMessage = (error as { message: string }).message;
      }

      // Use API error message if available, otherwise fall back to generic message
      if (errorMessage && errorMessage.trim()) {
        showError(errorMessage);
      } else {
        if (mode === 'create') {
          showError(TOAST_MESSAGES.ERROR_CREATE_RULE);
        } else if (mode === 'edit') {
          showError(TOAST_MESSAGES.ERROR_UPDATE_RULE);
        }
      }
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleDelete = () => {
    setIsDeleteModalOpen(true);
  };

  const handleCancel = () => {
    if (mode === 'edit' && isEditing && singleRule) {
      // Reset to original values from the store and exit edit mode
      setRuleName(singleRule.name);
      setReason(singleRule.reason);
      setAction(singleRule.action || '');
      setIsPayerSpecific(singleRule.markRuleForSpecificPayers);
      setPayers(singleRule.payerIds);
      setRuleStatus(singleRule.enabled ? 'active' : 'inactive');

      // Reset conditions to original values
      const transformedConditions = singleRule.matchBlocks.map((mb) => {
        // Handle source field - now it's an object with code property
        let sourceFieldCode = '';
        if (typeof mb.source === 'string') {
          // Legacy: Check if it's a UUID (contains hyphens) or a field code
          if (mb.source.includes('-')) {
            // It's a UUID, find the corresponding field code
            const field = fieldsData.find((f) => f.uuid === mb.source);
            sourceFieldCode = field?.code || mb.source;
          } else {
            // It's already a field code
            sourceFieldCode = mb.source;
          }
        } else {
          // New format: source is an object with code property
          sourceFieldCode = mb.source?.code || mb.source?.uuid || '';
        }

        // Handle operator field - now it's an object with uuid property
        let operatorUuid = '';
        if (typeof mb.operator === 'string') {
          // Legacy: operator is a string (UUID or code)
          operatorUuid = mb.operator;
        } else {
          // New format: operator is an object with uuid property
          operatorUuid = mb.operator?.uuid || '';
        }

        //changes added

        const condition: RuleCondition = {
          uuid: mb.uuid,
          field: [sourceFieldCode],
          operator: operatorUuid,
          value: mb.matchTexts?.join(', ') || '',
        };

        const inputType = getValueInputType(sourceFieldCode, operatorUuid);

        if (inputType === 'dropdown-claim') {
          condition.dropdownValue = mb.matchTexts?.[0] || '';
        } else if (inputType === 'dropdown-payer-multi') {
          condition.selectedPayers = mb.matchTexts || [];
        } else if (inputType === 'dropdown-provider') {
          //Added this
          condition.selectedProviders = mb.matchTexts || [];
        } else if (inputType === 'dropdown-credentials') {
          //Added this
          condition.selectedCredentials = mb.matchTexts || [];
        } else if (inputType === 'date-input') {
          // For date inputs, use the first matchText as the date value
          condition.value = mb.matchTexts?.[0] || '';
        }

        return condition;

        // return {
        //   uuid: mb.uuid, // Preserve UUID for existing matchBlocks
        //   field: [sourceFieldCode],
        //   operator: operatorUuid,
        //   value: mb.matchTexts?.join(', ') || '',
        // };
      });

      setConditions(
        transformedConditions.length > 0
          ? transformedConditions
          : [{ field: [], operator: '', value: '' }],
      );

      setIsEditing(false);
    } else {
      navigate(RouteConstants.ROUTE_HOME);
    }
  };

  const handleBack = () => {
    navigate(RouteConstants.ROUTE_HOME);
  };

  //TOFIX:Need in Future
  // const handleArchiveRule = () => {
  //   // Handle archive logic here
  //   console.log('Archiving rule:', id);
  //   // You can implement the actual archive functionality here
  // };

  const handleToggleRuleStatus = () => {
    // Toggle rule status functionality removed for now
    const newStatus = ruleStatus === 'active' ? 'inactive' : 'active';
    setRuleStatus(newStatus);
    // For now, just log the change since update functionality is not implemented
  };

  // const isFormValid =
  //   ruleName.trim() &&
  //   reason.trim() &&
  //   action.trim() &&
  //   conditions.every(
  //     (condition) => condition.field.length > 0 && condition.value.trim(),
  //   );

  //changes (16th)
  const isFormValid =
    ruleName.trim() &&
    reason.trim() &&
    action.trim() &&
    conditions.every((condition) => {
      if (condition.field.length === 0 || !condition.operator) return false;

      const inputType = getValueInputType(
        condition.field[0],
        condition.operator,
      );

      // if (inputType === 'dropdown-claim' || inputType === 'dropdown-payer') {
      //   return condition.dropdownValue && condition.dropdownValue.trim();
      // }

      // if (inputType === 'disabled-input') {
      //   return true; // Disabled inputs don't need validation
      // }

      if (inputType === 'dropdown-claim') {
        return condition.dropdownValue && condition.dropdownValue.trim();
      }

      if (inputType === 'dropdown-payer-multi') {
        return condition.selectedPayers && condition.selectedPayers.length > 0;
      }

      if (inputType === 'dropdown-provider') {
        // Added this
        return (
          condition.selectedProviders && condition.selectedProviders.length > 0
        );
      }

      if (inputType === 'dropdown-credentials') {
        // Added this
        return (
          condition.selectedCredentials &&
          condition.selectedCredentials.length > 0
        );
      }

      if (inputType === 'disabled-input') {
        return true;
      }

      if (inputType === 'date-input') {
        return condition.value && condition.value.trim();
      }

      return condition.value && condition.value.trim();
    });

  const canEdit = mode === 'edit' && !isEditing;
  const canSave = mode === 'create' || (mode === 'edit' && isEditing);
  const isFormDisabled = loading || singleRuleLoading || canEdit;

  const getProviderOptionsForField = (fieldCode: string) => {
    const selectedField = fieldsData.find((field) => field.code === fieldCode);

    if (!selectedField) return [];

    if (
      fieldCode === 'SUPERVISING_PROVIDER' &&
      selectedField.supervisingProviders
    ) {
      return selectedField.supervisingProviders
        .map((provider) => ({
          value: provider.npi,
          label: `${provider.firstName} ${provider.lastName}${provider.suffix ? `, ${provider.suffix}` : ''}`,
          uuid: provider.uuid,
        }))
        .sort((a, b) =>
          a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
        );
    }

    if (fieldCode === 'BILLING_PROVIDER' && selectedField.billingProviders) {
      return selectedField.billingProviders
        .map((provider) => ({
          value: provider.npi,
          label: `${provider.firstName} ${provider.lastName}${provider.suffix ? `, ${provider.suffix}` : ''}`,
          uuid: provider.uuid,
        }))
        .sort((a, b) =>
          a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
        );
    }

    if (
      fieldCode === 'RENDERING_PROVIDER' &&
      selectedField.renderingProviders
    ) {
      return selectedField.renderingProviders
        .map((provider) => ({
          value: provider.npi,
          label: `${provider.firstName} ${provider.lastName}${provider.suffix ? `, ${provider.suffix}` : ''}`,
          uuid: provider.uuid,
        }))
        .sort((a, b) =>
          a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
        );
    }

    if (
      fieldCode === 'REFERRING_PROVIDER' &&
      selectedField.referringProviders
    ) {
      return selectedField.referringProviders
        .map((provider) => ({
          value: provider.npi,
          label: `${provider.firstName} ${provider.lastName}${provider.suffix ? `, ${provider.suffix}` : ''}`,
          uuid: provider.uuid,
        }))
        .sort((a, b) =>
          a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
        );
    }

    return [];
  };

  // function to get credentials options
  const getCredentialsOptions = (operatorUuid: string) => {
    let credentialsArray: Array<{
      uuid: string;
      code: string;
      description: string;
      category: string;
    }> = [];

    fieldsData.forEach((field) => {
      if (field.operators) {
        field.operators.forEach((operator) => {
          if (operator.uuid === operatorUuid && 'credentials' in operator) {
            const operatorWithCredentials = operator as FieldOperators & {
              credentials: Array<{
                uuid: string;
                code: string;
                description: string;
                category: string;
              }>;
            };
            credentialsArray = operatorWithCredentials.credentials;
          }
        });
      }
    });

    return credentialsArray.map((credential) => ({
      value: credential.code,
      // label: `${credential.code} - ${credential.category}`,
      label: credential.code,
      uuid: credential.uuid,
    }));
  };

  return (
    <AppLayout currentPage="Dashboard">
      <Navbar showSearch={false} showAvatar={true} showWelcomeMessage={true} />

      <div className="p-6 mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={loading || singleRuleLoading}
            className="h-[40px] flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>

          <div className="flex items-center gap-2">
            {canEdit && (
              <>
                {canEditRule && (
                  <Button
                    variant="outline"
                    onClick={handleEdit}
                    disabled={loading || singleRuleLoading}
                    className="h-10 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Edit className="h-4 w-4" />
                    )}
                    Edit
                  </Button>
                )}
                {/* Removed standalone Delete button; moved into menu while editing */}
              </>
            )}

            {canSave && canEditRule && (
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={!isFormValid || loading || singleRuleLoading}
                className="h-10 ml-2 flex-1 sm:flex-none disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : mode === 'edit' && isEditing ? (
                  'Save Changes'
                ) : (
                  'Save'
                )}
              </Button>
            )}

            {mode === 'edit' && isEditing && (
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={loading || singleRuleLoading}
                className="h-10 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </Button>
            )}

            {canEditRule && isEditing && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={loading}
                    className="h-8 w-8 p-0 hover:bg-primary-base/5 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  sideOffset={12}
                  className="w-48"
                >
                  {canDeleteRule && (
                    <DropdownMenuItem
                      onClick={handleDelete}
                      disabled={loading || deleteLoading}
                    >
                      {deleteLoading ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="mr-2 h-4 w-4" />
                      )}
                      Delete rule
                    </DropdownMenuItem>
                  )}
                  {ruleStatus === 'active' ? (
                    <DropdownMenuItem
                      onClick={handleToggleRuleStatus}
                      disabled={loading}
                    >
                      <PowerOff className="mr-2 h-4 w-4" />
                      Make rule inactive
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem
                      onClick={handleToggleRuleStatus}
                      disabled={loading}
                    >
                      <Power className="mr-2 h-4 w-4" />
                      Make rule active
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>

        {/* Created/Updated info moved under conditions box */}

        {/* Error Display */}
        {/* {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Error creating rule
                </h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
              </div>
            </div>
          </div>
        )} */}

        {/* Single Rule Error Display */}
        {singleRuleError && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Error loading rule
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  {singleRuleError}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Loading State for Single Rule */}
        {singleRuleLoading && mode === 'edit' && (
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-3"></div>
              <div className="text-sm text-blue-800">Loading rule data...</div>
            </div>
          </div>
        )}

        {/* Rule Details Section */}
        <div className="space-y-6 mb-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rule name
              </label>
              <Input
                placeholder="Example: Missing total time in note"
                value={ruleName}
                onChange={(e) => setRuleName(e.target.value)}
                disabled={isFormDisabled}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason
                </label>
                <Textarea
                  placeholder="Why is this rule being made a rule?"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  disabled={isFormDisabled}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Action
                </label>
                <Textarea
                  placeholder="What should the user do when this rule is flagged?"
                  value={action}
                  onChange={(e) => setAction(e.target.value)}
                  rows={3}
                  disabled={isFormDisabled}
                />
              </div>
            </div>
          </div>
        </div>

        {/* TOFIX:Need in Future */}
        {/* Payer Specificity Section */}
        {/* <div className="mb-8">
          <div className="space-y-4">
            <div className="flex items-center">
              <Checkbox
                label="Make rule for specific payer(s)"
                checked={isPayerSpecific}
                onChange={(e) => setIsPayerSpecific(e.target.checked)}
                disabled={isFormDisabled}
              />
            </div>

            {isPayerSpecific && (
              <div className="w-full">
                <Typography
                  weight="medium"
                  size="12"
                  color="neutral900"
                  className="mb-2"
                >
                  Payer(s)
                </Typography>
                {payersLoading ? (
                  <div className="w-full p-4 text-center text-gray-500">
                    Loading payers...
                  </div>
                ) : payersError ? (
                  <div className="w-full p-4 text-center text-red-500">
                    Error loading payers: {payersError}
                  </div>
                ) : (
                  <MultiSelectChips
                    placeholder="Payers"
                    options={payerOptions}
                    selectedValues={payers}
                    onSelectionChange={setPayers}
                    className="w-full"
                    disabled={isFormDisabled}
                  />
                )}
              </div>
            )}
          </div>
        </div> */}

        {/* Rule Conditions Section */}
        <div className="mb-8">
          <div className="bg-neutral-50 border border-card-border rounded-lg p-6">
            <div className="flex items-center gap-2 mb-6">
              <img
                src={RuleConditionIcon}
                alt="Rule Condition"
                className="h-5 w-5"
              />
              <Typography color="neutral600" weight="medium" size="16">
                Rule conditions
              </Typography>
              <div className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded-full">
                {conditions.length}
              </div>
            </div>
            <div className="space-y-4">
              {conditions.map((condition, index) => (
                <React.Fragment key={index}>
                  <div className="bg-card border border-card-border rounded-lg p-7 relative ">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-gray-700 font-medium shrink-0">
                        If the
                      </span>

                      <div className="w-[180px] shrink-0">
                        {fieldsLoading ? (
                          <div className="w-full p-2 text-center text-gray-500 text-sm">
                            Loading fields...
                          </div>
                        ) : fieldsError ? (
                          <div className="w-full p-2 text-center text-red-500 text-sm">
                            Error: {fieldsError}
                          </div>
                        ) : (
                          <Select
                            value={condition.field[0] || undefined}
                            onValueChange={(value) =>
                              handleConditionChange(index, 'field', [value])
                            }
                            disabled={isFormDisabled}
                          >
                            <SelectTrigger
                              className="w-[180px] shrink-0 text-primary-base bg-white focus:ring-2 focus:ring-opacity-20 transition-colors p-1.5"
                              title={
                                condition.field[0]
                                  ? fieldOptions.find(
                                      (opt) => opt.value === condition.field[0],
                                    )?.label
                                  : 'Select a field'
                              }
                            >
                              <SelectValue
                                placeholder="Fields"
                                className="truncate"
                              />
                            </SelectTrigger>
                            <SelectContent>
                              {fieldOptions.map((opt) => (
                                <SelectItem
                                  key={opt.value}
                                  value={opt.value}
                                  className="truncate"
                                  title={opt.label}
                                >
                                  {/* {opt.label} */}
                                  <span className="block truncate">
                                    {opt.label}
                                  </span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>

                      <Select
                        key={condition.field[0] || 'no-field'}
                        value={condition.operator || undefined}
                        onValueChange={(value) =>
                          handleConditionChange(index, 'operator', value)
                        }
                        disabled={isFormDisabled}
                      >
                        <SelectTrigger
                          className="w-[180px] shrink-0 text-primary-base bg-white focus:ring-2 focus:ring-opacity-20 transition-colors"
                          // {/* <Typography
                          //   variant="small"
                          //   color="neutral900"
                          //   weight="medium"
                          //   size="14"
                          //   className="text-purple-1100"
                          // >
                          //   {condition.operator || 'Select operator'}
                          // </Typography> */}

                          title={
                            condition.operator
                              ? getOperatorsForField(condition.field[0]).find(
                                  (op) => op.value === condition.operator,
                                )?.label
                              : 'Select an operator'
                          }
                        >
                          <SelectValue
                            placeholder="Select operator"
                            className="truncate"
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {/*<SelectItem value="contain">contain</SelectItem>
                          <SelectItem value="doesn't contain">
                            doesn't contain
                          </SelectItem>*/}

                          {/* added this */}
                          {condition.field[0] &&
                            getOperatorsForField(condition.field[0]).map(
                              (operator) => (
                                <SelectItem
                                  key={operator.uuid}
                                  value={operator.value}
                                  className="truncate"
                                  title={operator.label}
                                >
                                  {/* {operator.label} */}
                                  <span className="block truncate">
                                    {operator.label}
                                  </span>
                                </SelectItem>
                              ),
                            )}

                          {/* Show placeholder if no field is selected */}
                          {!condition.field[0] && (
                            <SelectItem
                              value="operators"
                              disabled
                              className="truncate"
                            >
                              Please select a field first
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>

                      <span className="text-gray-700 font-medium shrink-0">
                        :
                      </span>

                      {/* changes conditional rendering */}
                      {(() => {
                        const inputType = getValueInputType(
                          condition.field[0],
                          condition.operator,
                        );
                        switch (inputType) {
                          case 'dropdown-claim':
                            // eslint-disable-next-line no-case-declarations
                            const claimOptions = getClaimOptions(
                              condition.field[0],
                              condition.operator,
                            );
                            return (
                              <div className="w-[180px] sm:flex-1 min-w-[150px] max-w-[800px]">
                                <Select
                                  value={condition.dropdownValue || undefined}
                                  onValueChange={(value) =>
                                    handleConditionChange(
                                      index,
                                      'dropdownValue',
                                      value,
                                    )
                                  }
                                  disabled={isFormDisabled}
                                >
                                  <SelectTrigger
                                    className="text-primary-base bg-white border border-gray-300 focus:border-primary-base focus:ring-primary-base focus:ring-2 focus:ring-opacity-20 focus:outline-none focus:shadow-none !focus-visible:border-primary-base !focus-visible:border-2"
                                    title={
                                      condition.dropdownValue
                                        ? claimOptions.find(
                                            (opt) =>
                                              opt.uuid ===
                                              condition.dropdownValue,
                                          )?.label
                                        : 'Select a reference'
                                    }
                                  >
                                    <SelectValue
                                      placeholder="Select reference"
                                      className="truncate"
                                    />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {claimOptions.length > 0 ? (
                                      claimOptions.map((option) => (
                                        <SelectItem
                                          key={option.uuid}
                                          value={option.uuid}
                                          className="truncate"
                                          title={option.label}
                                        >
                                          {/* {option.label} */}
                                          <span className="block truncate">
                                            {option.label}
                                          </span>
                                        </SelectItem>
                                      ))
                                    ) : (
                                      <SelectItem
                                        value="no-options"
                                        disabled
                                        className="truncate"
                                      >
                                        No options available
                                      </SelectItem>
                                    )}
                                  </SelectContent>
                                </Select>
                              </div>
                            );
                          case 'dropdown-payer-multi':
                            // eslint-disable-next-line no-case-declarations
                            const payerFieldOptions = getPayerOptionsForField();
                            return (
                              <div className="w-[180px] sm:flex-1 min-w-[150px] max-w-[800px]">
                                <MultiSelectChips
                                  placeholder="payers"
                                  options={payerFieldOptions}
                                  selectedValues={
                                    condition.selectedPayers || []
                                  }
                                  onSelectionChange={(values) =>
                                    handleConditionChange(
                                      index,
                                      'selectedPayers',
                                      values,
                                    )
                                  }
                                  className="text-primary-base bg-white border-gray-300 focus:border-primary-base focus:ring-primary-base focus:ring-2 focus:ring-opacity-20 focus:outline-none !focus:shadow-none !focus-visible:border-primary-base !focus-visible:border-2"
                                  disabled={isFormDisabled}
                                />
                              </div>
                            );
                          case 'disabled-input':
                            return (
                              <Input
                                placeholder="text/code"
                                value={condition.value}
                                onChange={(e) =>
                                  handleConditionChange(
                                    index,
                                    'value',
                                    e.target.value,
                                  )
                                }
                                className="w-[180px] sm:flex-1 min-w-[150px] max-w-[800px] text-primary-base bg-gray-100 border border-gray-300 focus:border-primary-base focus:ring-primary-base focus:ring-2 focus:ring-opacity-20 focus:outline-none focus:shadow-none !focus-visible:border-primary-base !focus-visible:border-2"
                                disabled={true}
                              />
                            );
                          case 'number-input':
                            return (
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="0.00"
                                value={condition.value}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  // Allow only numbers with up to 2 decimal places
                                  if (
                                    value === '' ||
                                    /^\d*\.?\d{0,2}$/.test(value)
                                  ) {
                                    handleConditionChange(
                                      index,
                                      'value',
                                      value,
                                    );
                                  }
                                }}
                                className="w-[180px] sm:flex-1 min-w-[150px] max-w-[800px] text-primary-base bg-white border border-gray-300 focus:border-primary-base focus:ring-primary-base focus:ring-2 focus:ring-opacity-20 focus:outline-none focus:shadow-none !focus-visible:border-primary-base !focus-visible:border-2"
                                disabled={isFormDisabled}
                              />
                            );
                          case 'dropdown-provider':
                            // eslint-disable-next-line no-case-declarations
                            const providerOptions = getProviderOptionsForField(
                              condition.field[0],
                            );
                            return (
                              <div className="w-[180px] sm:flex-1 min-w-[150px] max-w-[800px]">
                                <MultiSelectChips
                                  placeholder="providers"
                                  options={providerOptions}
                                  selectedValues={
                                    condition.selectedProviders || []
                                  }
                                  onSelectionChange={(values) =>
                                    handleConditionChange(
                                      index,
                                      'selectedProviders',
                                      values,
                                    )
                                  }
                                  className="text-primary-base bg-white border-gray-300 focus:border-primary-base focus:ring-primary-base focus:ring-2 focus:ring-opacity-20 focus:outline-none !focus:shadow-none !focus-visible:border-primary-base !focus-visible:border-2"
                                  disabled={isFormDisabled}
                                />
                              </div>
                            );

                          case 'dropdown-credentials':
                            // eslint-disable-next-line no-case-declarations
                            const credentialsOptions = getCredentialsOptions(
                              condition.operator,
                            );
                            return (
                              <div className="w-[180px] sm:flex-1 min-w-[150px] max-w-[800px]">
                                <MultiSelectChips
                                  placeholder="credentials"
                                  options={credentialsOptions}
                                  selectedValues={
                                    condition.selectedCredentials || []
                                  }
                                  onSelectionChange={(values) =>
                                    handleConditionChange(
                                      index,
                                      'selectedCredentials',
                                      values,
                                    )
                                  }
                                  className="text-primary-base bg-white border-gray-300 focus:border-primary-base focus:ring-primary-base focus:ring-2 focus:ring-opacity-20 focus:outline-none !focus:shadow-none !focus-visible:border-primary-base !focus-visible:border-2"
                                  disabled={isFormDisabled}
                                />
                              </div>
                            );
                          case 'date-input':
                            return (
                              <div className="relative w-[150px] shrink-0">
                                <Input
                                  type="date"
                                  placeholder="mm/dd/yyyy"
                                  value={condition.value}
                                  onChange={(e) =>
                                    handleConditionChange(
                                      index,
                                      'value',
                                      e.target.value,
                                    )
                                  }
                                  className="w-full text-primary-base bg-white border border-gray-300 focus:border-primary-base focus:ring-primary-base focus:ring-2 focus:ring-opacity-20 focus:outline-none focus:shadow-none !focus-visible:border-primary-base !focus-visible:border-2 pr-8"
                                  disabled={isFormDisabled}
                                />
                              </div>
                            );
                          default:
                            return (
                              <Input
                                placeholder="text/code"
                                value={condition.value}
                                onChange={(e) =>
                                  handleConditionChange(
                                    index,
                                    'value',
                                    e.target.value,
                                  )
                                }
                                className="w-[180px] sm:flex-1 min-w-[150px] max-w-[800px] text-primary-base bg-white border border-gray-300 focus:border-primary-base focus:ring-primary-base focus:ring-2 focus:ring-opacity-20 focus:outline-none focus:shadow-none !focus-visible:border-primary-base !focus-visible:border-2"
                                disabled={isFormDisabled}
                              />
                            );
                        }
                      })()}
                    </div>

                    {conditions.length > 1 && !canEdit && canEditRule && (
                      <button
                        onClick={() => handleRemoveCondition(index)}
                        disabled={loading}
                        className="absolute top-1/2 right-0 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ml-3"
                        title="Remove condition"
                      >
                        <Trash2 className="h-4 w-4 text-gray-500 hover:text-red-500" />
                      </button>
                    )}
                  </div>

                  {/* Add separator between conditions (except after the last one) */}
                  {index < conditions.length - 1 && (
                    <div className="relative flex items-center justify-center">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-primary-base/30"></div>
                      </div>
                      <div className="relative bg-neutral-50 px-4">
                        <span className="text-primary-base font-medium text-sm">
                          And
                        </span>
                      </div>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>

            <div className="flex justify-center mt-6">
              {canEditRule && (
                <Button
                  variant="primary"
                  onClick={handleAddCondition}
                  disabled={loading || singleRuleLoading || canEdit}
                  className="flex items-center gap-2 bg-black hover:bg-gray-800 px-6 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus className="h-4 w-4" />
                  Add condition
                </Button>
              )}
            </div>

            {/* Action Buttons */}
          </div>
          {/* Created/Updated info below conditions */}
          {mode === 'edit' && (
            <div className="mt-3 flex justify-end">
              <div className="text-right text-sx text-gray-600">
                <Typography variant="small" size="12" color="neutral400">
                  Created:{' '}
                  {singleRule?.createdAt
                    ? new Date(singleRule.createdAt).toLocaleString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })
                    : 'N/A'}
                </Typography>
                <Typography variant="small" size="12" color="neutral400">
                  Updated:{' '}
                  {singleRule?.updatedAt
                    ? new Date(singleRule.updatedAt).toLocaleString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })
                    : 'N/A'}
                </Typography>
              </div>
            </div>
          )}
        </div>
      </div>

      <DeleteRuleModal
        open={isDeleteModalOpen}
        onOpenChange={setIsDeleteModalOpen}
        ruleName={ruleName}
        ruleId={id || ''}
        tenantId={selectedHospital?.tenantId || ''}
        onDeleteSuccess={() => navigate(RouteConstants.ROUTE_HOME)}
      />
    </AppLayout>
  );
};

export default RuleForm;
